import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, ShieldCheck, MapPin } from 'lucide-react';
import { Button } from './ui/Button';
export const HeroSection: React.FC = () => {
  return <section className="relative pt-10 pb-8 md:pt-28 md:pb-24 overflow-hidden bg-background" data-id="element-192">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-193">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-3xl" data-id="element-194"></div>
        <div className="absolute top-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-3xl" data-id="element-195"></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-196">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-197">
          <div className="inline-block mb-2 md:mb-4 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-primary/10 border border-primary/20" data-id="element-198">
            <span className="text-primary font-semibold md:text-sm tracking-wide uppercase text-[10px]" data-id="element-199">
              PROFES DE TELAS RESUELTO™ — Sistema Completo
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 md:mb-8 font-serif" data-id="element-200">
            Todo tu año de <span className="text-primary" data-id="element-201">clases de telas</span>{' '}
            resuelto.
          </h1>
        </motion.div>

        {/* Product Image — 70% width, centered */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="flex justify-center mb-4 md:mb-8" data-id="element-202">
          <div className="w-[70%] drop-shadow-2xl" data-id="element-203">
            <img src="https://cdn.magicpatterns.com/uploads/c28To2uNpjr39xm3ECEotH/Diseno_sin_titulo-66.png" alt="Profes de Telas Resuelto Megapack" className="w-full h-auto object-contain" data-id="element-204" />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-center" data-id="element-205">
          <p className="md:text-2xl font-medium text-gray-700 mb-3 md:mb-6 text-[14px]" data-id="element-206">
            6 Tomos · 60+ Clases · 6 Bonos · Listo para usar.
          </p>

          <p className="md:text-lg text-gray-600 mb-4 md:mb-8 max-w-2xl mx-auto text-[14px]" data-id="element-207">
            Tu año completo de clases de telas listo: fundamentos, planificación
            por niveles, gestión, seguridad, marketing y mucho más. Sin
            improvisar nunca más.
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4 md:mb-8" data-id="element-208">
            {['PDF digital', 'Acceso inmediato', 'Todos los niveles', 'Garantía 7 días'].map((badge, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 md:px-3 md:py-1 bg-white border border-gray-200 rounded-full text-xs md:text-sm font-medium text-gray-700 shadow-sm" data-id="element-209">
                <CheckCircle2 size={14} className="text-accent" data-id="element-210" />
                {badge}
              </span>)}
          </div>

          <div className="flex flex-col items-center gap-3 md:gap-4" data-id="element-211">
            <Button href="#pricing" variant="primary" className="text-sm md:text-lg px-6 py-3 md:px-10 md:py-5" data-id="element-212">
              💥 QUIERO MI PROFES DE TELAS RESUELTO
            </Button>

            <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500 font-medium" data-id="element-213">
              <ShieldCheck size={16} className="text-green-600" data-id="element-214" />
              <span data-id="element-215">
                Pago 100% seguro · Mercado Pago | Tarjeta | Transferencia
              </span>
            </div>
          </div>
        </motion.div>

        {/* Social Proof Grid */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-8 md:mt-16" data-id="element-216">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-217">
            {/* +300 Profes */}
            <motion.div whileHover={{
            y: -2
          }} className="relative group bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 text-center overflow-hidden" data-id="element-218">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-219"></div>
              <div className="relative" data-id="element-220">
                <div className="mx-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2 md:mb-3" data-id="element-221">
                  <CheckCircle2 size={20} className="text-primary md:w-6 md:h-6" data-id="element-222" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900 leading-none mb-1" data-id="element-223">
                  +300
                </div>
                <div className="text-xs md:text-sm text-gray-500 font-medium" data-id="element-224">
                  profes confían
                </div>
              </div>
            </motion.div>

            {/* 6+6 Tomos + Bonos */}
            <motion.div whileHover={{
            y: -2
          }} className="relative group bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 text-center overflow-hidden" data-id="element-225">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-226"></div>
              <div className="relative" data-id="element-227">
                <div className="mx-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-accent/10 flex items-center justify-center mb-2 md:mb-3" data-id="element-228">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-accent md:w-6 md:h-6" data-id="element-229">
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" data-id="element-230" />
                  </svg>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900 leading-none mb-1" data-id="element-231">
                  6+6
                </div>
                <div className="text-xs md:text-sm text-gray-500 font-medium" data-id="element-232">
                  Tomos + Bonos
                </div>
              </div>
            </motion.div>

            {/* 4.9/5 Rating */}
            <motion.div whileHover={{
            y: -2
          }} className="relative group bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 text-center overflow-hidden" data-id="element-233">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-234"></div>
              <div className="relative" data-id="element-235">
                <div className="mx-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-yellow-50 flex items-center justify-center mb-2 md:mb-3" data-id="element-236">
                  <Star size={20} fill="#facc15" className="text-yellow-400 md:w-6 md:h-6" data-id="element-237" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900 leading-none mb-1" data-id="element-238">
                  4.9
                  <span className="text-base md:text-lg text-gray-400 font-medium" data-id="element-239">
                    /5
                  </span>
                </div>
                <div className="flex justify-center gap-0.5 mb-1" data-id="element-240">
                  {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="#facc15" className="text-yellow-400" data-id="element-241" />)}
                </div>
                <div className="text-xs md:text-sm text-gray-500 font-medium" data-id="element-242">
                  valoración
                </div>
              </div>
            </motion.div>

            {/* Todo el país */}
            <motion.div whileHover={{
            y: -2
          }} className="relative group bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 text-center overflow-hidden" data-id="element-243">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-244"></div>
              <div className="relative" data-id="element-245">
                <div className="mx-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-50 flex items-center justify-center mb-2 md:mb-3" data-id="element-246">
                  <MapPin size={20} className="text-blue-500 md:w-6 md:h-6" data-id="element-247" />
                </div>
                <div className="text-2xl md:text-3xl leading-none mb-1" data-id="element-248">🇦🇷</div>
                <div className="text-xs md:text-sm text-gray-500 font-medium" data-id="element-249">
                  Todo el país
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
};